import * as StoreTypes from "./DataTypes/storeTypes";
import * as moment from "moment";
import { EnumValues } from "../Common/enumHelper";
import * as LocalStorage from "../Common/localStorageSessionManager";

function getDefaultState(settings: StoreTypes.IInitialSettings): StoreTypes.IStore {
    const data: StoreTypes.IStore = {
        initialSettings: {
            ...settings,
        },
        common: {
            isPageLoading: false,
            currentStep: StoreTypes.Step.Location,
            isUserLoggedIn: settings.isUserLoggedIn,
            loggedInClientId: settings.loggedInClientId,
            isBusy: false,
            cancelRequested: false,
            clients: [],
        },
        servicesData: { services: [], saleableCategories: [] },
        locations: [],
        providers: [],
        availability: null,
        stepsInfo: {},
        submitForm: {},
    };

    const steps = EnumValues.getValues(StoreTypes.Step);

    for (let step of steps) {
        data.stepsInfo[step] = {};
    }

    const confirmationStep = data.stepsInfo[StoreTypes.Step.Confirmation] as StoreTypes.IConfirmationStepInfo;
    confirmationStep.clientId = settings.loggedInClientId;

    const availabilityStep = data.stepsInfo[StoreTypes.Step.When] as StoreTypes.IAvailabilityStepInfo;
    availabilityStep.start = moment().startOf("month");

    return data;
}

interface ILocalStorageObject {
    stepsInfo: StoreTypes.IStepsInfo;
    currentStep: StoreTypes.Step;
    bookedAppointment?: any;
}

function getSerialisedDate(date?: moment.Moment) {
    if (!date) {
        return null;
    }

    return date.format("YYYY-MM-DDTHH:mm:ss");
}

function getSerialisable(state: StoreTypes.IStore): ILocalStorageObject {
    const stepsInfo: StoreTypes.IStepsInfo = { ...state.stepsInfo };

    const whenSource = state.stepsInfo[StoreTypes.Step.When] as StoreTypes.IAvailabilityStepInfo;

    const whenSerialisable: StoreTypes.IAvailabilityStepInfo = {
        ...whenSource,
        start: getSerialisedDate(whenSource.start) as any,
        selectedDate: getSerialisedDate(whenSource.selectedDate) as any,
        navigationDate: getSerialisedDate(whenSource.navigationDate) as any,
    };

    if (whenSource.slot) {
        whenSerialisable.slot = {
            ...whenSource.slot,
            date: getSerialisedDate(whenSource.slot.date) as any,
        };
    }

    stepsInfo[StoreTypes.Step.When] = whenSerialisable;

    const localStorage: ILocalStorageObject = {
        stepsInfo: stepsInfo,
        currentStep: state.common.currentStep,
        bookedAppointment: state.common.bookedAppointment,
    };

    return localStorage;
}

function updateFromLocalStorage(state: StoreTypes.IStore, data: ILocalStorageObject) {
    const when = data.stepsInfo[StoreTypes.Step.When] as StoreTypes.IAvailabilityStepInfo;

    when.start = moment(when.start);
    when.selectedDate = when.selectedDate ? moment(when.selectedDate) : null;
    when.navigationDate = when.navigationDate ? moment(when.navigationDate) : null;

    if (when.slot) {
        when.slot.date = when.slot.date ? moment.utc(when.slot.date, "YYYY-MM-DDTHH:mm:ss") : null;
    }

    // copy the client ID from the state, because if they just logged in, we don't want to overwrite
    // with what is in LocalStorage
    const confirmationStep = data.stepsInfo[StoreTypes.Step.Confirmation] as StoreTypes.IConfirmationStepInfo;
    confirmationStep.clientId = (
        state.stepsInfo[StoreTypes.Step.Confirmation] as StoreTypes.IConfirmationStepInfo
    ).clientId;

    state.stepsInfo = data.stepsInfo;

    state.common.currentStep = data.currentStep;
    state.common.bookedAppointment = data.bookedAppointment;
}

export function loadState(settings: StoreTypes.IInitialSettings): StoreTypes.IStore {
    const state = getDefaultState(settings);

    const localStorageManager = LocalStorage.getLocalStorageSessionManager(
        settings.accountInitialSettings.organisationInfo.sgId
    );

    const data = localStorageManager.getData<ILocalStorageObject>(LocalStorage.SessionDataType.BookingData);

    if (data === null) {
        return state;
    }

    updateFromLocalStorage(state, data);

    return state;
}

export function saveState(state: StoreTypes.IStore, settings: StoreTypes.IInitialSettings) {
    const serialisable = getSerialisable(state);

    const localStorageManager = LocalStorage.getLocalStorageSessionManager(
        settings.accountInitialSettings.organisationInfo.sgId
    );

    localStorageManager.setData(LocalStorage.SessionDataType.BookingData, serialisable);
}

export function flush(sgId: string) {
    const localStorageManager = LocalStorage.getLocalStorageSessionManager(sgId);

    localStorageManager.setData(LocalStorage.SessionDataType.BookingData, null);
}

export function resetState(settings: StoreTypes.IInitialSettings): StoreTypes.IStore {
    flush(settings.accountInitialSettings.organisationInfo.sgId);

    return loadState(settings);
}
